import { Table, Input, Button, Space, Tag } from "antd";
import { CheckCircleFilled, WarningFilled, SearchOutlined } from "@ant-design/icons";
import { useState, useRef } from "react";
import moment from "moment";
import { Link } from "react-router-dom";

export default function VerificationsIndex(props) {
  const { verifications, loading, setSelectedVerification, onDataChange } = props;
  const searchInput = useRef(null);

  const getColumnSearchProps = (dataIndex, title) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Buscar ${title}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reiniciar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const columns = [
    {
      title: <span style={{ fontWeight: 700, color: "#1890ff" }}>Folio</span>,
      dataIndex: "ControlInterno",
      key: "ControlInterno",
      width: 160,
      fixed: "left",
      render: (text, record) => {
        return (
          <Link to={`/verificaciones/${record.VerificacionID}`}>
            {record.FolioAmuvie ? (
              <>
                <CheckCircleFilled style={{ color: "#52c41a", marginRight: "10px" }} />
                {text}
              </>
            ) : (
              <>
                <WarningFilled style={{ color: "orange", marginRight: "10px" }} />
                {text}
              </>
            )}
          </Link>
        );
      },
      sorter: (a, b) => (a.ControlInterno || "").localeCompare(b.ControlInterno || ""),
      ...getColumnSearchProps("ControlInterno", "folio"),
      filters: [
        { text: "Con Folio Amuvie", value: "check" },
        { text: "Sin Folio Amuvie", value: "warn" },
      ],
      onFilter: (value, record) => (value === "check" ? !!record.FolioAmuvie : !record.FolioAmuvie),
    },
    {
      title: <span style={{ fontWeight: 700, color: "#1890ff" }}>Fecha</span>,
      dataIndex: "FechaCreacion",
      key: "FechaCreacion",
      width: 120,
      responsive: ["md"],
      render: (text) => {
        if (text === null) {
          return <span className="subtle-text">N/D</span>;
        } else {
          return moment.utc(text).format("DD/MM/YYYY");
        }
      },
      sorter: (a, b) => moment(a.FechaCreacion).unix() - moment(b.FechaCreacion).unix(),
    },
    {
      title: <span style={{ fontWeight: 700, color: "#1890ff" }}>Descripcion</span>,
      dataIndex: "DescripcionBreve",
      key: "DescripcionBreve",
      width: 200,
      responsive: ["lg"],
      render: (text) => {
        if (text === null) {
          return <span className="subtle-text">N/D</span>;
        } else {
          return <span className="break-by-word">{text}</span>;
        }
      },
      sorter: (a, b) => (a.DescripcionBreve || "").localeCompare(b.DescripcionBreve || ""),
      ...getColumnSearchProps("DescripcionBreve", "descripción"),
    },
    {
      title: <span style={{ fontWeight: 700, color: "#1890ff" }}>Cliente</span>,
      dataIndex: "NombreCliente",
      key: "NombreCliente",
      render: (text) => {
        if (text === null) {
          return <span className="subtle-text">N/D</span>;
        } else {
          return <span className="break-by-word">{text}</span>;
        }
      },
      sorter: (a, b) => (a.NombreCliente || "").localeCompare(b.NombreCliente || ""),
      ...getColumnSearchProps("NombreCliente", "cliente"),
    },
    {
      title: <span style={{ fontWeight: 700, color: "#1890ff" }}>Razón Social</span>,
      dataIndex: "NombreCompleto",
      key: "NombreCompleto",
      responsive: ["lg"],
      render: (text) => {
        if (text === null) {
          return <span className="subtle-text">N/D</span>;
        } else {
          return <span className="break-by-word">{text}</span>;
        }
      },
      sorter: (a, b) => (a.NombreCompleto || "").localeCompare(b.NombreCompleto || ""),
      ...getColumnSearchProps("NombreCompleto", "razón social"),
    },
    {
      title: <span style={{ fontWeight: 700, color: "#1890ff" }}>Tensión (V)</span>,
      dataIndex: "TensionElectricaSuministro",
      key: "TensionElectricaSuministro",
      align: "right",
      width: 100,
      responsive: ["xxl"],
      render: (text) => {
        if (text === null) {
          return <span className="subtle-text">N/D</span>;
        } else {
          return text.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        }
      },
      sorter: (a, b) => (a.TensionElectricaSuministro || 0) - (b.TensionElectricaSuministro || 0),
    },
    {
      title: <span style={{ fontWeight: 700, color: "#1890ff" }}>Carga (kW)</span>,
      dataIndex: "CargaInstalada",
      key: "CargaInstalada",
      align: "right",
      width: 100,
      responsive: ["xxl"],
      render: (text) => {
        if (text === null) {
          return <span className="subtle-text">N/D</span>;
        } else {
          return Number(text).toFixed(2);
        }
      },
      sorter: (a, b) => (a.CargaInstalada || 0) - (b.CargaInstalada || 0),
    },
    {
      title: <span style={{ fontWeight: 700, color: "#1890ff" }}>Alcance (kW)</span>,
      dataIndex: "AlcanceVerificacion",
      key: "AlcanceVerificacion",
      align: "right",
      width: 100,
      responsive: ["xxl"],
      render: (text) => {
        if (text === null) {
          return <span className="subtle-text">N/D</span>;
        } else {
          return Number(text).toFixed(2);
        }
      },
      sorter: (a, b) => (a.AlcanceVerificacion || 0) - (b.AlcanceVerificacion || 0),
    },
    {
      title: <span style={{ fontWeight: 700, color: "#1890ff" }}>Capacidad (kVA)</span>,
      dataIndex: "Capacidad",
      key: "Capacidad",
      align: "right",
      width: 100,
      responsive: ["xxl"],
      render: (text) => {
        if (text === null) {
          return <span className="subtle-text">N/D</span>;
        } else {
          return Number(text).toFixed(2);
        }
      },
      sorter: (a, b) => (a.Capacidad || 0) - (b.Capacidad || 0),
    },
    {
      title: <span style={{ fontWeight: 700, color: "#1890ff" }}>Monto</span>,
      dataIndex: "Monto",
      key: "Monto",
      align: "right",
      width: 110,
      fixed: "right",
      responsive: ["xxl"],
      render: (text) => {
        if (text === null) {
          return <span className="subtle-text">N/D</span>;
        } else {
          return (
            "$" +
            Number(text)
              .toFixed(2)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          );
        }
      },
      sorter: (a, b) => (a.Monto || 0) - (b.Monto || 0),
    },
  ];

  return (
    <div style={{ fontSize: "13px" }}>
      <Table
        size="small"
        onChange={(pagination, filters, sorter, extra) => {
          if (onDataChange) {
            onDataChange(extra.currentDataSource);
          }
        }}
        rowSelection={{
          type: "radio",
          onSelect: (record) => {
            setSelectedVerification(record.VerificacionID);
          },
        }}
        columns={columns}
        dataSource={verifications}
        loading={loading}
        rowKey="VerificacionID"
        pagination={{
          defaultPageSize: 25,
          showSizeChanger: true,
          showLessItems: true,
          pageSizeOptions: ["10", "25", "50", "100"],
        }}
        scroll={{
          x: "max-content",
        }}
      />
    </div>
  );
}
